import classNames from 'classnames'
import React from 'react'

import type { LinkAttachmentCta } from '../../types'

import type { LinkAttachmentVariant } from './CardShell'
import { normalizeExternalHref } from './normalizeExternalHref'

export interface CardCtaProps {
  variant: LinkAttachmentVariant
  cta: LinkAttachmentCta
  /**
   * Full-width pill (the default — LinkAttachment's single footer button).
   * `false` sizes the button to its content, for an inline multi-action footer
   * row (RichCard's `secondary` footer).
   */
  fullWidth?: boolean
  /**
   * Omit the default `mt-2` top margin when the caller owns spacing — e.g. a
   * multi-action footer that spaces its buttons with its own `gap`. Defaults to
   * `false`, preserving LinkAttachment's button-below-description offset.
   */
  standalone?: boolean
}

type CtaEmphasis = NonNullable<LinkAttachmentCta['variant']>

// Two axes: the bubble side (`dark` = sent chrome, `light` = received chrome)
// crossed with CTA emphasis (`primary` = filled, `secondary` = subtle/grey).
// `primary` preserves the existing filled button exactly, so callers that omit
// `cta.variant` are unaffected. Values are explicit rather than DS tokens: the
// component-library preset has no neutral button token (`primary` is brand
// purple), and the host preset can't retheme arbitrary hex. Align the grey with
// the Arbor secondary token when the shared design tokens land.
const BUTTON_CLASS_BY_VARIANT: Record<
  LinkAttachmentVariant,
  Record<CtaEmphasis, string>
> = {
  dark: {
    primary: 'bg-white text-[#121110] hover:bg-white/90',
    secondary: 'bg-white/15 text-white hover:bg-white/25',
  },
  light: {
    primary: 'bg-[#121110] text-white hover:bg-[#2a2928]',
    secondary: 'bg-black/[0.06] text-[#121110] hover:bg-black/10',
  },
}

/**
 * Pill-shaped CTA rendered below the description on Link App cards that
 * surface an action instead of a URL (e.g. FAQ "View FAQs", Form "Complete form").
 * Renders as `<a target="_blank">` when `cta.href` is set, otherwise as a
 * plain `<button>`.
 */
const CardCta: React.FC<CardCtaProps> = ({
  variant,
  cta,
  fullWidth = true,
  standalone = false,
}) => {
  const emphasis: CtaEmphasis = cta.variant ?? 'primary'
  const className = classNames(
    'inline-flex h-10 items-center justify-center rounded-full px-4 text-sm font-medium leading-none transition-colors',
    fullWidth ? 'w-full' : 'min-w-0',
    !standalone && 'mt-2',
    BUTTON_CLASS_BY_VARIANT[variant][emphasis]
  )

  // A content-width button (RichCard's `secondary` row, which sits beside the
  // copy in a fixed-width card) can be squeezed by a long or localized label —
  // truncate to an ellipsis instead of overflowing and getting clipped by the
  // card. The full-width button (LinkAttachment's default) spans its own row
  // and never needs this, so it is left exactly as-is.
  const labelNode = fullWidth ? (
    cta.label
  ) : (
    <span className="min-w-0 truncate">{cta.label}</span>
  )

  // Mirror the URL normalization used by the shell anchor so bare
  // hostnames (e.g. `tr.ee/foo`) open as external links rather than
  // resolving against the current host.
  const normalizedHref = normalizeExternalHref(cta.href)

  if (normalizedHref) {
    return (
      <a
        href={normalizedHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          // Stop the click from bubbling up to the card's anchor wrapper
          // (Received variant) so we don't navigate twice.
          e.stopPropagation()
          cta.onClick?.()
        }}
        className={`${className} no-underline`}
      >
        {labelNode}
      </a>
    )
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        cta.onClick?.()
      }}
      className={className}
    >
      {labelNode}
    </button>
  )
}

export default CardCta
